// client/src/cognitive-flows/components/shared/blocks/ResultBlocks.js
//
// Preconfigured analysis-block renderers shared by RunResultModal and the
// unified chat. Data contract matches geminiService's json_media shape:
// { table, callouts, Chart_data: { chart_header, chart_type, charx_axis_data_points, chart_y_axis_data_points }, image }.

import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  AreaChart,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { formatNumber } from '../../canvas/chartResolve';

const styles = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '24px' },
  th: { padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-secondary)' },
  td: { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: 'var(--text-primary)' },
  callouts: { background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', fontSize: '13px', lineHeight: 1.7, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', marginBottom: '24px' },
  sectionTitle: { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '10px' },
};

export function CalloutsBlock({ text }) {
  if (!text) return null;
  return <div style={styles.callouts}>{text}</div>;
}

export function ResultTable({ rows }) {
  if (!rows || rows.length === 0) return <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No table data.</p>;
  const headers = Object.keys(rows[0]);
  return (
    <table style={styles.table}>
      <thead>
        <tr>{headers.map((h) => <th key={h} style={styles.th}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>{headers.map((h) => <td key={h} style={styles.td}>{row[h]}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * `Chart_data.chart_series` — the OPTIONAL multi-series field a canvas chart
 * emits when it plots across several data sources. Absent everywhere else
 * (workflow results, chat artifacts, every chart written before it existed),
 * which is why the single-series path below stays exactly as it was.
 *
 * Returns `[{ name, points }]`, or null when there is nothing multi about it.
 */
function multiSeriesOf(chartData) {
  const raw = chartData?.chart_series;
  if (!Array.isArray(raw) || raw.length < 2) return null;
  const clean = raw
    .filter((sr) => sr && Array.isArray(sr.points))
    .map((sr, i) => ({ name: String(sr.name || `Series ${i + 1}`), points: sr.points }));
  return clean.length >= 2 ? clean : null;
}

export function normalizeChartPoints(chartData) {
  const x = chartData?.charx_axis_data_points;
  const y = chartData?.chart_y_axis_data_points;

  const xArr = Array.isArray(x) ? x : [];
  const yArr = Array.isArray(y) ? y : [];

  // Support y as [number] or [[...]] (multiple series). If multiple series, we render first.
  const y0 = Array.isArray(yArr[0]) ? (yArr[0] || []) : yArr;

  const n = Math.min(xArr.length, y0.length);
  const data = [];
  for (let i = 0; i < n; i++) {
    const xv = xArr[i];
    const yv = y0[i];
    const yn = typeof yv === 'number' ? yv : Number(yv);
    data.push({ x: String(xv ?? ''), y: Number.isFinite(yn) ? yn : null });
  }
  return data;
}

/**
 * One recharts row per x value, carrying every series as its own key.
 *
 * A missing point stays `null` rather than becoming 0 — recharts breaks the
 * line there, which is the honest rendering of "this source has no reading for
 * that day". Filling zeros would draw a confident dip to the axis.
 */
export function normalizeMultiSeriesPoints(chartData, series) {
  const xArr = Array.isArray(chartData?.charx_axis_data_points) ? chartData.charx_axis_data_points : [];
  return xArr.map((xv, i) => {
    const row = { x: String(xv ?? '') };
    series.forEach((sr, si) => {
      const v = sr.points[i];
      const n = typeof v === 'number' ? v : Number(v);
      row[`y${si}`] = v === null || v === undefined || !Number.isFinite(n) ? null : n;
    });
    return row;
  });
}

// Series colours, in order. Chosen to stay distinguishable in the PDF/PNG
// exports and for the common red-green deficiencies, not just on screen.
const SERIES_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#a855f7', '#ef4444', '#06b6d4', '#84cc16', '#ec4899'];

/**
 * Optional presentation fields on Chart_data, added so a canvas chart can be
 * labelled by hand: `x_axis_label`, `y_axis_label`, `series_name`,
 * `show_legend`.
 *
 * All four are OPTIONAL and every default reproduces the previous rendering
 * exactly — an axis with no label draws as it always did, `show_legend`
 * absent still shows the legend, and `series_name` absent still reads "Value".
 * That matters because this module is shared with the chat surface and the
 * workflow result renderers, which have never sent these fields and must not
 * change.
 */
// Shared chrome for both branches — the same values ChartBlock/PieBlock have
// always used inline, lifted so the look branch draws the same frame.
const chartOuter = {
  border: '1px solid var(--border)',
  borderRadius: '12px',
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
  padding: '14px',
  marginBottom: '24px',
};
const chartTitle = { fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' };

/** Every series of a chart in one shape: multi when it is multi, else the single y as one series. */
function seriesListOf(chartData) {
  const multi = multiSeriesOf(chartData);
  if (multi) return multi;
  const yRaw = Array.isArray(chartData?.chart_y_axis_data_points) ? chartData.chart_y_axis_data_points : [];
  return [{ name: chartData?.series_name ? String(chartData.series_name) : 'Value', points: Array.isArray(yRaw[0]) ? yRaw[0] : yRaw }];
}

function legendProps(position) {
  return {
    verticalAlign: position === 'top' ? 'top' : position === 'right' ? 'middle' : 'bottom',
    align: position === 'right' ? 'right' : 'center',
    layout: position === 'right' ? 'vertical' : 'horizontal',
  };
}

// A pie draws its labels OUTSIDE the arc, and a legend takes plot height, so a
// fixed 100px radius with no margin ran off the top of the 280px box the
// moment a legend sat under it. The radius is a share of the plot instead
// (recharts sizes a percentage against the smaller half-dimension), and the
// margin reserves the band the label lines need — the same courtesy the
// cartesian path pays its axis titles.
const PIE_MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
const PIE_OUTER_RADIUS_PCT = 72;
const pieRadius = (share = 1) => `${Math.round(PIE_OUTER_RADIUS_PCT * share)}%`;

/**
 * The look branch for pies and donuts. `look.colors` is one hex per SLICE
 * (the resolver sizes it that way for pie/donut).
 */
function LookPie({ chartData }) {
  const look = chartData.look;
  const data = normalizeChartPoints(chartData).filter((d) => d.y !== null && d.y >= 0);
  if (!data.length) return null;
  const fmt = (v) => formatNumber(v, look.format);
  const label = look.dataLabels || look.pie.percentLabels
    ? (p) => (look.pie.percentLabels ? `${Math.round((p.percent || 0) * 100)}%` : fmt(p.value))
    : false;
  // Donut hole as the same share of the plot the outer radius uses, so the
  // ring keeps its proportions at every card size.
  const inner = look.type === 'donut' ? pieRadius(look.pie.donutWidth) : 0;
  return (
    <div style={chartOuter} data-testid="canvas-look-chart" data-look-type={look.type}>
      <div style={chartTitle}>{chartData.chart_header ? String(chartData.chart_header) : 'Chart'}</div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <PieChart margin={PIE_MARGIN}>
            <Pie data={data} dataKey="y" nameKey="x" outerRadius={pieRadius()} innerRadius={inner} paddingAngle={inner ? 1 : 0} label={label}>
              {data.map((_, i) => <Cell key={i} fill={look.colors[i]} />)}
            </Pie>
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: '10px', borderColor: '#e2e8f0' }} />
            {look.legend.show && <Legend {...legendProps(look.legend.position)} />}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/**
 * The look branch for every cartesian kind (bar / line / area / combo).
 *
 * The category axis is X unless the bar is horizontal, where recharts wants
 * `layout="vertical"` and the axes swap roles — `look.xLabel` still names the
 * category axis and `look.yLabel` the value axis, wherever they end up drawn.
 * Axes are built as a keyed array, not a fragment: recharts finds its
 * children by type and a fragment would hide them.
 */
function LookChart({ chartData }) {
  const look = chartData.look;
  if (look.type === 'pie' || look.type === 'donut') return <LookPie chartData={chartData} />;
  const series = seriesListOf(chartData);
  const data = normalizeMultiSeriesPoints(chartData, series);
  if (!data.length) return null;
  const fmt = (v) => formatNumber(v, look.format);
  const horizontal = look.type === 'bar' && look.horizontal;
  const Frame = look.type === 'combo' ? ComposedChart : look.type === 'area' ? AreaChart : look.type === 'line' ? LineChart : BarChart;
  const tick = { fill: '#475569', fontSize: 12 };
  const axisLine = { stroke: '#cbd5e1' };
  const axisLabelStyle = { fill: '#475569', fontSize: 11, fontWeight: 600 };
  const angled = !horizontal && look.xTickAngle !== 0;
  const catLabel = look.xLabel
    ? (horizontal
      ? { value: look.xLabel, angle: -90, position: 'insideLeft', style: { ...axisLabelStyle, textAnchor: 'middle' } }
      : { value: look.xLabel, position: 'insideBottom', offset: -4, style: axisLabelStyle })
    : undefined;
  const valLabel = look.yLabel
    ? (horizontal
      ? { value: look.yLabel, position: 'insideBottom', offset: -4, style: axisLabelStyle }
      : { value: look.yLabel, angle: -90, position: 'insideLeft', style: { ...axisLabelStyle, textAnchor: 'middle' } })
    : undefined;
  // 0, not 'auto', for the floor: recharts' own default domain is [0, 'auto'],
  // so an untouched axis matches the legacy chart.
  const domain = [look.yMin ?? 0, look.yMax ?? 'auto'];
  const axes = horizontal
    ? [
      <XAxis key="val" type="number" tick={tick} tickFormatter={fmt} domain={domain} axisLine={axisLine} tickLine={axisLine} label={valLabel} />,
      <YAxis key="cat" type="category" dataKey="x" width={110} tick={tick} axisLine={axisLine} tickLine={axisLine} label={catLabel} />,
    ]
    : [
      <XAxis key="cat" dataKey="x" tick={tick} axisLine={axisLine} tickLine={axisLine} label={catLabel}
        angle={angled ? look.xTickAngle : 0} textAnchor={angled ? 'end' : 'middle'}
        height={angled ? (look.xTickAngle === -90 ? 70 : 50) : 30} interval={angled ? 0 : 'preserveEnd'} />,
      <YAxis key="val" tick={tick} tickFormatter={fmt} domain={domain} axisLine={axisLine} tickLine={axisLine} label={valLabel} />,
    ];
  const margin = {
    top: look.dataLabels ? 18 : 8,
    right: look.legend.position === 'right' ? 8 : 16,
    left: (horizontal ? look.xLabel : look.yLabel) ? 12 : 0,
    bottom: ((horizontal ? look.yLabel : look.xLabel) ? 22 : 8) + (angled ? (look.xTickAngle === -90 ? 40 : 28) : 0),
  };
  const marks = series.map((sr, i) => {
    const key = `y${i}`;
    const color = look.colors[i];
    const kind = look.type === 'combo' ? (look.seriesKinds[i] || 'bar') : look.type;
    const labels = look.dataLabels
      ? <LabelList dataKey={key} position={horizontal ? 'right' : 'top'} formatter={fmt} style={{ fontSize: 10, fill: '#475569' }} />
      : null;
    // Same chrome as the legacy branch, so a colour change changes only colour.
    if (kind === 'line') {
      // connectNulls stays FALSE by default: a gap means this source reported
      // nothing that day, and bridging it would invent a reading.
      return (
        <Line key={key} type={look.curve} dataKey={key} name={sr.name} stroke={color} strokeWidth={3}
          dot={look.dots} connectNulls={look.connectNulls}>{labels}</Line>
      );
    }
    if (kind === 'area') {
      return (
        <Area key={key} type={look.curve} dataKey={key} name={sr.name} stroke={color} fill={color} fillOpacity={0.25}
          strokeWidth={2} dot={look.dots} connectNulls={look.connectNulls} stackId={look.stacked ? 's' : undefined}>{labels}</Area>
      );
    }
    return (
      <Bar key={key} dataKey={key} name={sr.name} fill={color} stackId={look.stacked ? 's' : undefined}
        radius={look.stacked ? 0 : horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0]}>{labels}</Bar>
    );
  });
  return (
    <div style={chartOuter} data-testid="canvas-look-chart" data-look-type={look.type}>
      <div style={chartTitle}>{chartData.chart_header ? String(chartData.chart_header) : 'Chart'}</div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <Frame data={data} margin={margin} layout={horizontal ? 'vertical' : 'horizontal'}>
            {look.grid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
            {axes}
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: '10px', borderColor: '#e2e8f0' }} labelStyle={{ color: '#0f172a', fontWeight: 700 }} />
            {look.legend.show && <Legend {...legendProps(look.legend.position)} />}
            {marks}
          </Frame>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ChartBlock({ chartData }) {
  if (!chartData || typeof chartData !== 'object') return null;
  if (chartData.look) return <LookChart chartData={chartData} />;
  const type = String(chartData.chart_type || 'line').toLowerCase();
  const header = chartData.chart_header ? String(chartData.chart_header) : 'Chart';
  const series = multiSeriesOf(chartData);
  const data = series ? normalizeMultiSeriesPoints(chartData, series) : normalizeChartPoints(chartData);
  const xLabel = chartData.x_axis_label ? String(chartData.x_axis_label) : '';
  const yLabel = chartData.y_axis_label ? String(chartData.y_axis_label) : '';
  const seriesName = chartData.series_name ? String(chartData.series_name) : 'Value';
  // `!== false`, not truthiness: an absent field must keep the legend, and only
  // an explicit false hides it.
  const showLegend = chartData.show_legend !== false;

  if (!data.length) return null;

  const outer = {
    border: '1px solid var(--border)',
    borderRadius: '12px',
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
    padding: '14px',
    marginBottom: '24px',
  };
  const title = { fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' };

  const axisLabelStyle = { fill: '#475569', fontSize: 11, fontWeight: 600 };
  const common = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
      <XAxis dataKey="x" tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} tickLine={{ stroke: '#cbd5e1' }}
        label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -4, style: axisLabelStyle } : undefined} />
      <YAxis tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} tickLine={{ stroke: '#cbd5e1' }}
        label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', style: { ...axisLabelStyle, textAnchor: 'middle' } } : undefined} />
      <Tooltip
        contentStyle={{ borderRadius: '10px', borderColor: '#e2e8f0' }}
        labelStyle={{ color: '#0f172a', fontWeight: 700 }}
      />
      {showLegend && <Legend />}
    </>
  );

  // An axis label needs room that the tick row does not: without the extra
  // bottom/left margin recharts draws it clipped at the container edge.
  const margin = { top: 8, right: 16, left: yLabel ? 12 : 0, bottom: xLabel ? 22 : 8 };

  return (
    <div style={outer}>
      <div style={title}>{header}</div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          {type === 'bar' ? (
            <BarChart data={data} margin={margin}>
              {common}
              {series
                ? series.map((sr, i) => (
                  <Bar key={sr.name + i} dataKey={`y${i}`} name={sr.name}
                    fill={SERIES_COLORS[i % SERIES_COLORS.length]} radius={[8, 8, 0, 0]} />
                ))
                : <Bar dataKey="y" name={seriesName} fill="#3b82f6" radius={[8, 8, 0, 0]} />}
            </BarChart>
          ) : (
            <LineChart data={data} margin={margin}>
              {common}
              {series
                ? series.map((sr, i) => (
                  // connectNulls stays FALSE: a gap means this source reported
                  // nothing that day, and bridging it would invent a reading.
                  <Line key={sr.name + i} type="monotone" dataKey={`y${i}`} name={sr.name}
                    stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={3} dot={false} connectNulls={false} />
                ))
                : <Line type="monotone" dataKey="y" name={seriesName} stroke="#3b82f6" strokeWidth={3} dot={false} />}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ImageBlock({ base64 }) {
  if (!base64 || typeof base64 !== 'string') return null;
  const b64 = base64.trim();
  if (!b64) return null;

  // If AI returns a full data URL, keep it. Else assume png.
  const src = b64.startsWith('data:image/') ? b64 : `data:image/png;base64,${b64}`;

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={styles.sectionTitle}>Image</div>
      <div style={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
        <img src={src} alt="AI generated" style={{ width: '100%', display: 'block' }} />
      </div>
    </div>
  );
}

const PIE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

export function PieBlock({ chartData }) {
  if (!chartData || typeof chartData !== 'object') return null;
  if (chartData.look) return <LookPie chartData={chartData} />;
  const data = normalizeChartPoints(chartData).filter((d) => d.y !== null && d.y >= 0);
  if (!data.length) return null;
  const outer = {
    border: '1px solid var(--border)', borderRadius: '12px',
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
    padding: '14px', marginBottom: '24px',
  };
  const title = { fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' };
  return (
    <div style={outer}>
      <div style={title}>{chartData.chart_header ? String(chartData.chart_header) : 'Chart'}</div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <PieChart margin={PIE_MARGIN}>
            <Pie data={data} dataKey="y" nameKey="x" outerRadius={pieRadius()} label>
              {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: '10px', borderColor: '#e2e8f0' }} />
            {/* A pie's legend names the SLICES (nameKey="x"), so series_name
                and the axis labels have nothing to say here — only the
                show/hide applies. */}
            {chartData.show_legend !== false && <Legend />}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
